import { useState, useRef } from "react";
import { cn } from "../lib/utils";

const DEFAULT_PRESETS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899",
  "#64748b", "#0f172a", "#f8fafc", "#ffffff",
];

interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  presets?: string[];
  className?: string;
}

function isValidHex(hex: string) {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex);
}

function normalizeHex(raw: string): string {
  const cleaned = raw.startsWith("#") ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{3}$/.test(cleaned)) {
    const [, r, g, b] = cleaned;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return cleaned.slice(0, 7);
}

export function ColorPicker({ value, onChange, presets = DEFAULT_PRESETS, className }: ColorPickerProps) {
  const [inputVal, setInputVal] = useState(value);
  const nativeRef = useRef<HTMLInputElement>(null);

  const handlePreset = (hex: string) => {
    setInputVal(hex);
    onChange(hex);
  };

  const handleInput = (raw: string) => {
    // Allow typing freely; only fire onChange when valid
    const typed = raw.startsWith("#") ? raw : `#${raw}`;
    setInputVal(typed);
    if (isValidHex(typed)) {
      onChange(normalizeHex(typed));
    }
  };

  const handleBlur = () => {
    if (!isValidHex(inputVal)) {
      setInputVal(value);
    }
  };

  const handleNative = (hex: string) => {
    setInputVal(hex);
    onChange(hex);
  };

  return (
    <div className={cn("color-picker", className)}>
      <div className="color-picker-swatches">
        {presets.map((hex) => (
          <button
            key={hex}
            className={cn("color-swatch", normalizeHex(value).toLowerCase() === normalizeHex(hex).toLowerCase() && "color-swatch-active")}
            style={{ background: hex }}
            onClick={() => handlePreset(hex)}
            aria-label={hex}
            title={hex}
          />
        ))}
      </div>
      <div className="color-picker-input-row">
        <button
          className="color-picker-preview"
          style={{ background: isValidHex(inputVal) ? inputVal : value }}
          onClick={() => nativeRef.current?.click()}
          aria-label="Open color picker"
          title="Click to pick any color"
        />
        <input
          ref={nativeRef}
          type="color"
          className="color-picker-native"
          value={isValidHex(inputVal) ? normalizeHex(inputVal) : value}
          onChange={(e) => handleNative(e.target.value)}
          tabIndex={-1}
          aria-hidden="true"
        />
        <span className="color-picker-hash">#</span>
        <input
          className="color-picker-hex-input"
          value={inputVal.replace(/^#/, "")}
          onChange={(e) => handleInput(e.target.value)}
          onBlur={handleBlur}
          maxLength={7}
          spellCheck={false}
          aria-label="Hex color value"
          placeholder="000000"
        />
      </div>
    </div>
  );
}
